import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreens } from 'components/Navigation/types';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ALERT, doToast, NAVIGATE_MODAL } from 'store/actions';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Icon, ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import {
  filterArrayEmpty,
  formatDate,
  isoDateStringToDate,
  openUrl,
  urlWithQuery,
} from 'lib/riskbase';
import { ScreenSize, UI_DATE_FORMAT_LONG, UI_TIME_FORMAT } from 'lib/constants';
import { capitalize } from 'lodash';
import {
  CommunicationAccessedFrom,
  CommunicationStatus,
  CommunicationType,
  CommunicationLinkStatus,
} from 'types/Engage';
import type { EngageCommunicationListItem } from 'types/Engage';
import { selectEngageFlat } from 'store/remote/engageSelectors';
import { Button } from 'components/Button';
import { IconButton } from 'components/IconButton';
import { PageScrollView } from 'components/Page';
import * as Clipboard from 'expo-clipboard';
import { useScreenSize } from 'hooks/useScreenSize';

export const EngageFlatModalCommunicationsPage: React.FC<{
  pageKey: string;
  HeaderItems: React.ReactNode;
  contactId: string | null;
}> = ({ pageKey: key, HeaderItems, contactId }) => {
  const dispatch = useDispatch();
  const { contacts, communications } = useSelector((state) =>
    selectEngageFlat(state, key),
  )!;
  const { bottom: safeAreaBottom } = useSafeAreaInsets();
  const isWeb = useScreenSize() === ScreenSize.large;
  const scrollPaddingBottom = isWeb
    ? theme.paddingPageVertical * 10 + safeAreaBottom
    : theme.paddingPageVertical * 10 + safeAreaBottom;

  const filteredCommunications = contactId
    ? communications.filter(
        (communication) => communication.contactId === contactId,
      )
    : communications;

  const handleSendCommunication = React.useCallback(() => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageSendCommunication,
        params: { key, contactId },
      }),
    );
  }, [contactId, dispatch, key]);

  const handlePreviewCommunication = (
    communication: EngageCommunicationListItem,
  ) => {
    if (!communication.previewContentURL) {
      dispatch(
        ALERT({
          text: 'Preview Not Available',
        }),
      );
      return;
    }

    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.PDFVIEWER,
        params: {
          title: communication.name,
          sourceUrl: communication.previewContentURL,
        },
      }),
    );
  };

  const handleLink = async (link: string, copy: boolean) => {
    const finalLink = urlWithQuery(link, {
      source: CommunicationAccessedFrom.app,
    });
    if (copy) {
      const copied = await Clipboard.setStringAsync(finalLink);
      if (copied) {
        dispatch(doToast('Link Copied'));
        return;
      }
    }
    openUrl(finalLink);
  };

  return (
    <View style={{ flex: 1 }}>
      <PageScrollView
        isInModal={true}
        ignoreSafeAreaBottom
        contentPaddingBottom={scrollPaddingBottom}
        header={HeaderItems}
      >
        <Card>
          {filteredCommunications.length === 0 ? (
            <CardRow value="No Messages Sent" />
          ) : (
            filteredCommunications.map((communication) => {
              const address = communication.address
                .split('\\n')
                .filter(filterArrayEmpty)
                .join(', ');
              const isLinkExpired =
                communication.linkStatus === CommunicationLinkStatus.expired;
              const communicationContact = communication.contactId
                ? (contacts.find((c) => c.id === communication.contactId) ?? {
                    firstName: 'Archived Contact',
                    lastName: '',
                  })
                : null;
              const TxtStatus = (
                <Txt
                  style={{
                    color: theme.colors.fieldValueText,
                  }}
                  size="xsmall"
                >
                  {formatDate(
                    isoDateStringToDate(communication.createdDate),
                    UI_DATE_FORMAT_LONG,
                  )}
                  {communication.status !== CommunicationStatus.requested &&
                    ` • ${capitalize(communication.status)} ${formatDate(
                      isoDateStringToDate(communication.statusDate),
                      UI_DATE_FORMAT_LONG,
                    )} at ${formatDate(
                      isoDateStringToDate(communication.statusDate),
                      UI_TIME_FORMAT,
                    )}`}
                </Txt>
              );
              return (
                <CardRow
                  key={communication.id}
                  labelAlignTop
                  showMenuOnPress
                  showMenuOnContext
                  menuOptions={[
                    {
                      text: 'Preview Message',
                      onPress: () => handlePreviewCommunication(communication),
                    },
                    ...(communication.link && !isLinkExpired
                      ? [
                          communication.link,
                          {
                            text: 'Open Link',
                            onPress: () =>
                              handleLink(communication.link!, false),
                            icon: ICONS.ArrowUpRightFromSquareLight,
                          },
                          {
                            text: 'Copy Link',
                            onPress: () =>
                              handleLink(communication.link!, true),
                            icon: ICONS.CopyLight,
                          },
                        ]
                      : ['Link is no longer available']),
                  ]}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      columnGap: theme.paddingHorizontal,
                      flexWrap: 'wrap',
                    }}
                  >
                    <View
                      style={{
                        alignItems: 'center',
                        width: 72,
                        paddingTop: 4.5,
                      }}
                    >
                      <View
                        style={{
                          borderRadius: 42,
                          width: 42,
                          height: 42,
                          backgroundColor: theme.colors.black10,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {communication.type === CommunicationType.email ? (
                          <Icon source={ICONS.AtLight} />
                        ) : (
                          <Icon source={ICONS.FileLight} />
                        )}
                      </View>
                      <View
                        style={{
                          marginTop: 6,
                          backgroundColor:
                            communication.linkStatus ===
                            CommunicationLinkStatus.expired
                              ? theme.colors.red
                              : theme.colors.green,
                          paddingHorizontal: 8,
                          paddingVertical: 3,
                          borderRadius: 6,
                        }}
                      >
                        <Txt
                          size="xxxsmall"
                          weight="medium"
                          style={{
                            color: theme.colors.white,
                            textTransform: 'uppercase',
                          }}
                        >
                          {communication.linkStatus}
                        </Txt>
                      </View>
                    </View>
                    <View style={{ flex: 1 }}>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          marginBottom: 2,
                          columnGap: theme.paddingHorizontalHalf,
                        }}
                      >
                        <Txt key="name" weight="medium" style={{ flex: 1 }}>
                          {communication.name}
                        </Txt>
                      </View>
                      <Txt size="xsmall">
                        {communicationContact
                          ? `${communicationContact.firstName} ${communicationContact.lastName}`.trim() +
                            ` <${address}>`
                          : address}
                      </Txt>
                      <Txt
                        style={{
                          color: theme.colors.fieldValueText,
                        }}
                        size="xsmall"
                      >
                        {TxtStatus}
                      </Txt>

                      {communication.linkAccessed && (
                        <Txt
                          style={{
                            color: theme.colors.fieldValueText,
                          }}
                          size="xsmall"
                        >
                          Link Opened{' '}
                          {formatDate(
                            isoDateStringToDate(
                              communication.linkAccessed.date,
                            ),
                            UI_DATE_FORMAT_LONG,
                          )}{' '}
                          at{' '}
                          {formatDate(
                            isoDateStringToDate(
                              communication.linkAccessed.date,
                            ),
                            UI_TIME_FORMAT,
                          )}
                        </Txt>
                      )}
                    </View>
                  </View>
                </CardRow>
              );
            })
          )}
        </Card>
      </PageScrollView>
      {isWeb ? (
        <View pointerEvents="box-none" style={styles.webButtonWrapper}>
          <IconButton
            icon={ICONS.PaperPlaneLight}
            onPress={handleSendCommunication}
            size={60}
            iconSize={30}
            color={theme.colors.white}
            style={styles.webButton}
            hoveredStyle={{ backgroundColor: theme.colors.blue90 }}
            pressedStyle={{ backgroundColor: theme.colors.blue80 }}
          />
        </View>
      ) : (
        <View style={styles.mobileBarWrapper}>
          <View
            style={{
              backgroundColor: 'transparent',
              paddingBottom: theme.paddingVerticalHalf,
              borderRadius: 8,
            }}
          >
            <Button
              iconRightSize={20}
              iconRight={ICONS.PaperPlaneLight}
              text="Send Message"
              textSize="large"
              onPress={handleSendCommunication}
              style={styles.mobileBarButton}
            />
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  webButtonWrapper: {
    position: 'absolute',
    right: theme.paddingPageVertical * 2,
    bottom: theme.paddingPageVertical * 2,
    zIndex: 2,
  },
  webButton: {
    backgroundColor: theme.colors.blue,
    shadowColor: theme.colors.black,
    shadowOpacity: 0.35,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 8,
    elevation: 4,
  },
  mobileBarWrapper: {
    position: 'absolute',
    width: '70%',
    left: '15%',
    bottom: 0,
    zIndex: 2,
    shadowRadius: 4,
    shadowOpacity: 0.4,
    shadowOffset: { width: 0, height: 3 },
    marginBottom: theme.paddingPageVertical,
  },
  mobileBarButton: {
    justifyContent: 'center',
    height: 50,
    borderRadius: 999,
  },
});
